import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dimensions } from 'components/visuals';
import RedirectToProductList from './components/redirectToProductList';
import MACPageHeader from './components/macPageHeader';
import { BillingContactForm } from './components/BillingContactForm';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectContactsForOrganisation,
  selectCountriesByName,
  selectGetCustomerByEmail,
  selectSwitchedUser,
  getCurrentMACContactOrgCountry,
} from './duck/selectors';
import { fetchContactsForOrganisation, fetchGetCustomerByEmail } from './duck/actions/contact';
import { State } from '../../model/State';
import Spinner from '@atlaskit/spinner';
import { BillingOrgContactForm } from './components/BillingOrgContactForm';
import { RESTRICTED_COUNTRIES_ISO_CODES, RESTRICTED_EMAIL_DOMAINS } from '../utils';
import { extractTLDFrom } from '../siteconfigurator/utils';
import { fetchCountryByName } from './duck/actions/country';

export const Section = styled.div`
  margin-bottom: ${Dimensions.units(5)}px;
  width: ${Dimensions.pageWidth / 2}px;
`;

export const MACEditDetailsPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const userEmail = useSelector(selectSwitchedUser);
  const customerByEmail = useSelector((state) =>
    selectGetCustomerByEmail(state as State)?.[userEmail]?.ifSuccess((r) => r),
  );
  const customerPartnerId = customerByEmail?.organisationDetails?.id;
  const contactsForOrganisation = useSelector((state) =>
    selectContactsForOrganisation(state as State, customerPartnerId),
  );

  const currentContactOrgCountryISO = useSelector(getCurrentMACContactOrgCountry);
  const orgCountriesFromAccounts = useSelector(selectCountriesByName) || {};

  const [isRuByRestricted, setIsRuByRestricted] = useState(false);

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'mac.edit-details.title' });
  }, []);

  useEffect(() => {
    if (userEmail) {
      dispatch(fetchGetCustomerByEmail(userEmail));
    }
  }, [userEmail]);

  useEffect(() => {
    if (customerByEmail) {
      dispatch(fetchContactsForOrganisation(customerPartnerId));
      const mainContactOrgCountry = customerByEmail?.organisationDetails?.country;

      if (mainContactOrgCountry) {
        dispatch(fetchCountryByName(mainContactOrgCountry));
      }
    }
  }, [customerByEmail]);

  useEffect(() => {
    const countryISOCodesFromAccounts = Object.values(orgCountriesFromAccounts).map(
      ({ _result }) => _result?.countryIsoCode,
    );

    const isISORestricted = [
      currentContactOrgCountryISO,
      ...countryISOCodesFromAccounts,
    ].some((item) => RESTRICTED_COUNTRIES_ISO_CODES.includes(item));

    const isEmailRestricted = RESTRICTED_EMAIL_DOMAINS.includes(extractTLDFrom(userEmail));

    if (isISORestricted || isEmailRestricted) {
      setIsRuByRestricted(true);
    } else {
      setIsRuByRestricted(false);
    }
  }, [currentContactOrgCountryISO, userEmail, orgCountriesFromAccounts]);

  return !customerByEmail ? (
    <Spinner size="large" />
  ) : (
    <>
      <RedirectToProductList />
      <MACPageHeader />
      <Section>
        <BillingContactForm
          {...customerByEmail?.contactDetails}
          isRuByRestricted={isRuByRestricted}
        />
        <BillingOrgContactForm
          {...customerByEmail?.contactDetails}
          {...customerByEmail?.organisationDetails}
          isRuByRestricted={isRuByRestricted}
          orgContacts={contactsForOrganisation}
        />
      </Section>
    </>
  );
};
